(() => {
  const section = document.querySelector('[data-product-section]');
  const form = section?.querySelector('[data-product-form]');
  if (!form) return;
  const variants = JSON.parse(section.querySelector('[data-variants]').textContent);
  const nativeMaterial = section.hasAttribute('data-film-native');
  if (nativeMaterial) variants.forEach(variant => {
    const [size, material, finish] = variant.options;
    const [frame, mount = 'No passepartout'] = finish.split(' / ');
    variant.options = [material, size, mount, material === 'Canvas' ? 'Unframed' : frame];
  });
  const selector = form.querySelector('[data-variant-id]');
  const groups = [...form.querySelectorAll('[data-option-group]')];
  const price = section.querySelector('[data-price]');
  const availability = section.querySelector('[data-availability]');
  const error = form.querySelector('[data-product-error]');
  const button = form.querySelector('[data-add-button]');
  const buttonLabel = button.querySelector('[data-add-label]');
  const quantity = form.querySelector('input[name=quantity]');
  const formatter = new Intl.NumberFormat(section.dataset.locale || 'en', {
    style: 'currency', currency: section.dataset.currency || 'USD'
  });
  const money = cents => formatter.format(cents / 100);
  const current = () => variants.find(v => String(v.id) === String(selector?.value || form.querySelector('input[name=id]:checked')?.value));
  const values = () => groups.map(group => group.querySelector('input:checked')?.value);
  const matching = options => variants.find(v => v.options.every((value, i) => value === options[i]));
  const isUnframed = value => /^(unframed|no frame|without frame|print only|无框|不含框)$/i.test(value.trim());
  const frameTone = value => isUnframed(value) ? 'none' : /black|黑/i.test(value) ? 'black' : /white|白/i.test(value) ? 'white' : /wood|oak|walnut|natural|木|橡/i.test(value) ? 'wood' : 'neutral';
  const artwork = section.querySelector('[data-art-frame]');
  const artworkImage = artwork?.querySelector('img');
  const artworkLink = artwork?.closest('a');
  const originalArtwork = artworkImage && {
    src: artworkImage.getAttribute('src'), srcset: artworkImage.getAttribute('srcset'),
    href: artworkLink?.getAttribute('href')
  };
  const imagePath = url => url?.replace(/^https?:/, '').split('?')[0];
  const mat = artwork?.querySelector('[data-art-mat]');
  const originalRatio = artwork?.style.getPropertyValue('--art-ratio');
  const sizePair = value => String(value || '').match(/(\d+(?:\.\d+)?)\s*[x×]\s*(\d+(?:\.\d+)?)/i)?.slice(1).map(Number);
  let busy = false;
  let preferredMatIndex = null;

  function renderArtwork(variant, selected) {
    if (!artworkImage) return;
    const frameIndex = groups.findIndex(group => group.dataset.optionKind === 'frame');
    const frameValue = selected[frameIndex];
    const tone = nativeMaterial && selected[0] === 'Canvas' ? 'none' : frameValue ? frameTone(frameValue) : 'none';
    // A dedicated variant photograph takes precedence; do not draw a second frame over it.
    const hasVariantImage = variant?.image && imagePath(variant.image) !== imagePath(originalArtwork.src);
    const source = hasVariantImage ? variant.image : originalArtwork.src;
    if (artworkImage.getAttribute('src') !== source) {
      artworkImage.setAttribute('src', source);
      if (hasVariantImage || !originalArtwork.srcset) artworkImage.removeAttribute('srcset');
      else artworkImage.setAttribute('srcset', originalArtwork.srcset);
      if (artworkLink) artworkLink.setAttribute('href', hasVariantImage ? variant.image : originalArtwork.href);
    }
    artwork.dataset.frame = hasVariantImage ? 'none' : tone;
    const matValue = selected[groups.findIndex(group => group.dataset.optionKind === 'mat')];
    const printSize = sizePair(selected[groups.findIndex(group => group.dataset.optionKind === 'size')]);
    const outerSize = sizePair(matValue);
    const showMat = Boolean(mat && !hasVariantImage && printSize && outerSize);
    if (mat) {
      mat.dataset.active = String(showMat);
      if (showMat) {
        mat.style.setProperty('--mat-ratio', outerSize[0] / outerSize[1]);
        mat.style.setProperty('--print-width', (printSize[0] / outerSize[0] * 100) + '%');
        mat.style.setProperty('--print-height', (printSize[1] / outerSize[1] * 100) + '%');
        artwork.style.setProperty('--art-ratio', outerSize[0] / outerSize[1]);
      } else artwork.style.setProperty('--art-ratio', originalRatio);
    }
    artwork.setAttribute('aria-label', tone === 'none' ? 'Unframed artwork' : `${frameValue} preview`);
  }

  function updateButton() {
    const variant = current();
    const count = Number(quantity.value);
    button.disabled = busy || !variant?.available || variant.max === 0;
    if (!buttonLabel) return;
    if (busy) buttonLabel.textContent = 'Adding…';
    else if (!variant) buttonLabel.textContent = 'Unavailable';
    else if (!variant.available || variant.max === 0) buttonLabel.textContent = button.dataset.soldOut;
    else buttonLabel.textContent = `${button.dataset.addText} · ${money(variant.cents * (Number.isInteger(count) && count > 0 ? count : 1))}`;
  }

  function render() {
    const variant = current();
    const selected = values();
    if (nativeMaterial) {
      const canvas = selected[0] === 'Canvas';
      groups.forEach(group => { if (['mat', 'frame'].includes(group.dataset.optionKind)) group.hidden = canvas; });
      section.querySelector('[data-film-canvas-note]').hidden = !canvas;
      section.querySelector('[data-film-material-label]').textContent = canvas ? 'Material: Canvas' : 'Material: Photo Paper';
    }
    renderArtwork(variant, selected);
    price.textContent = variant ? variant.price : 'Unavailable';
    availability.textContent = variant?.available ? 'Available to order' : 'Currently unavailable';
    section.querySelector('.availability-dot')?.classList.toggle('is-unavailable', !variant?.available);
    if (variant?.max != null) {
      quantity.max = variant.max;
      quantity.title = variant.max ? `${variant.max} available` : 'Out of stock';
    } else {
      quantity.removeAttribute('max');
      quantity.removeAttribute('title');
    }
    groups.forEach((group, index) => {
      group.querySelector('[data-option-selected]').textContent = (selected[index] || '').split(' (')[0];
      const frame = group.dataset.optionKind === 'frame';
      const mount = group.dataset.optionKind === 'mat';
      const frameIndex = groups.findIndex(g => g.dataset.optionKind === 'frame');
      const sizeIndex = groups.findIndex(g => g.dataset.optionKind === 'size');
      const mountBaseline = mount && variants.find(v => v.options[sizeIndex] === selected[sizeIndex] && v.options[index] === 'No passepartout' && isUnframed(v.options[frameIndex]));
      const baseline = frame && variants.find(v => isUnframed(v.options[index]) && v.options.every((value, i) => i === index || value === selected[i]));
      group.querySelectorAll('[data-option-input]').forEach(input => {
        const prefixMatches = variants.filter(v => v.options[index] === input.value && v.options.every((value, i) => i >= index || value === selected[i]));
        input.disabled = prefixMatches.length === 0 || !prefixMatches.some(v => v.available);
        const candidate = matching(selected.map((value, i) => i === index ? input.value : value));
        const soldOut = prefixMatches.length > 0 && !prefixMatches.some(v => v.available);
        const card = input.closest('.product-option-card');
        card.classList.toggle('is-sold-out', soldOut);
        card.hidden = mount && prefixMatches.length === 0;
        const note = card.querySelector('[data-option-note]');
        let message = soldOut ? 'Sold out' : input.disabled ? 'Unavailable' : '';
        if (!message && frame && candidate) {
          if (baseline && candidate.cents >= baseline.cents) {
            message = isUnframed(input.value) ? 'Included' : candidate.cents === baseline.cents ? 'No extra charge' : `+${money(candidate.cents - baseline.cents)}`;
          } else message = money(candidate.cents);
        }
        if (!message && mount && mountBaseline) {
          const plain = prefixMatches.find(v => isUnframed(v.options[frameIndex]));
          if (plain) message = plain.cents === mountBaseline.cents ? 'Included' : '+' + money(plain.cents - mountBaseline.cents);
        }
        if (frame && !input.disabled && !isUnframed(input.value)) {
          const selectedMat = selected[groups.findIndex(g => g.dataset.optionKind === 'mat')];
          const fittedSize = sizePair(selectedMat) || sizePair(selected[sizeIndex]);
          if (fittedSize) message += ' · ' + fittedSize.join(' x ') + ' cm';
        }
        note.textContent = message;
        note.hidden = !message;
        const swatch = card.querySelector('[data-frame-swatch]');
        if (swatch) swatch.dataset.tone = isUnframed(input.value) ? 'unframed' : frameTone(input.value);
      });
    });
    updateButton();
  }

  form.addEventListener('change', event => {
    if (busy) return;
    if (event.target.matches('[data-option-input]')) {
      const selected = values();
      const changedIndex = Number(event.target.closest('[data-option-group]').dataset.optionIndex);
      const sizeIndex = groups.findIndex(group => group.dataset.optionKind === 'size');
      const matIndex = groups.findIndex(group => group.dataset.optionKind === 'mat');
      if (sizeIndex >= 0 && matIndex >= 0) {
        const matsFor = options => [...new Set(variants
          .filter(v => v.options.every((value, i) => i === matIndex || value === options[i]))
          .map(v => v.options[matIndex]))].filter(value => value !== 'No passepartout');
        if (changedIndex === matIndex) {
          preferredMatIndex = selected[matIndex] === 'No passepartout' ? null : matsFor(selected).indexOf(selected[matIndex]);
        } else if (changedIndex === sizeIndex && selected[matIndex] !== 'No passepartout') {
          const previous = current()?.options;
          const mounts = matsFor(selected);
          if (previous && mounts.length) {
            // Keep the mat choice even though its outer dimensions change with the print size.
            if (preferredMatIndex === null) preferredMatIndex = Math.max(0, matsFor(previous).indexOf(previous[matIndex]));
            selected[matIndex] = mounts[Math.min(preferredMatIndex, mounts.length - 1)];
          }
        }
      }
      // Preserve the complete choice whenever it exists; otherwise resolve later options only.
      // Material changes keep the chosen size while resolving Canvas's fixed finish.
      const candidates = variants.filter(v => v.options.every((value, i) => i > changedIndex || value === selected[i]) &&
        (!nativeMaterial || changedIndex !== 0 || v.options[1] === selected[1]));
      const score = v => v.options.reduce((total, value, i) => total + (i > changedIndex && value === selected[i] ? 1 : 0), 0);
      const preferred = [...candidates].sort((a,b) => score(b) - score(a));
      const variant = matching(selected) || preferred.find(v => v.available) || preferred[0];
      selector.value = variant ? String(variant.id) : '';
      if (variant) groups.forEach((group, index) => group.querySelectorAll('input').forEach(input => { input.checked = input.value === variant.options[index]; }));
      error.textContent = '';
      const url = new URL(location.href);
      if (variant) url.searchParams.set('variant', variant.id);
      else url.searchParams.delete('variant');
      history.replaceState({}, '', url);
      render();
    } else if (event.target.name === 'id') render();
    else if (event.target.name === 'quantity') updateButton();
  });
  quantity.addEventListener('input', updateButton);

  form.addEventListener('submit', async event => {
    event.preventDefault();
    if (busy) return;
    const variant = current();
    const count = Number(quantity.value);
    if (!variant?.available || variant.max === 0) {
      error.textContent = 'This combination is currently unavailable.';
      return;
    }
    if (!Number.isInteger(count) || count < 1 || (variant.max != null && count > variant.max)) {
      error.textContent = 'Please choose an available quantity.';
      return;
    }
    busy = true;
    error.textContent = '';
    groups.forEach(group => { group.disabled = true; });
    updateButton();
    try {
      const response = await fetch((document.body.dataset.root || '/') + 'cart/add.js', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: [{ id: variant.id, quantity: count }] })
      });
      if (!response.ok) {
        const result = await response.json();
        throw new Error(typeof result.description === 'string' ? result.description : 'This print could not be added right now.');
      }
      const announcer = document.querySelector('[data-announcer]');
      if (announcer) announcer.textContent = 'Print added to your bag';
      document.dispatchEvent(new CustomEvent('cart:refresh'));
      document.querySelector('[data-open="CartDrawer"]')?.click();
    } catch (err) {
      error.textContent = err.message || 'This print could not be added right now. Please try again.';
    } finally {
      busy = false;
      groups.forEach(group => { group.disabled = false; });
      render();
    }
  });
  render();
  // Retired variant links fall back to a valid option; keep shared URLs in sync.
  const initialUrl = new URL(location.href);
  const initialVariant = current();
  if (initialUrl.searchParams.has('variant') && initialVariant && initialUrl.searchParams.get('variant') !== String(initialVariant.id)) {
    initialUrl.searchParams.set('variant', initialVariant.id);
    history.replaceState(history.state, '', initialUrl);
  }
  const optionCards = form.querySelector('[data-product-options]');
  if (optionCards) optionCards.hidden = false;
  if (selector) form.querySelector('[data-variant-fallback]').hidden = true;
})();
